import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import BrandLogo from './BrandLogo'

type Props = {
  title: string
  lastUpdated: string
  children: ReactNode
}

export default function LegalPageLayout({ title, lastUpdated, children }: Props) {
  return (
    <div className="min-h-screen bg-white dark:bg-[#13120A]">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header with Logo and Back Button */}
        <div className="mb-12 pb-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between mb-6">
            <Link to="/login" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0"></div>
              <BrandLogo className="h-3 w-auto text-gray-900 dark:text-white" />
            </Link>
            
            <Link
              to="/login"
              className="flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-blue-600 rounded p-2 -mr-2"
              aria-label="Go back to home"
            >
              <span className="text-sm">Back</span>
            </Link>
          </div>
        </div>

        {/* Title */}
        <header className="mb-12">
          <h1 className="text-3xl font-semibold mb-4 text-gray-900 dark:text-white">{title}</h1>
          <p className="text-sm text-gray-500">
            Last updated: <time dateTime={lastUpdated}>{lastUpdated}</time>
          </p>
        </header>

        {/* Content */}
        <article className="space-y-8 text-gray-700 dark:text-gray-300 leading-relaxed">{children}</article>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800">
          <nav aria-label="Legal navigation">
            <ul className="flex flex-wrap gap-6 text-sm">
              <li>
                <Link
                  to="/privacy-policy"
                  className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-gray-500 rounded"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-of-service"
                  className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-gray-500 rounded"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/accessibility-statement"
                  className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-gray-500 rounded"
                >
                  Accessibility Statement
                </Link>
              </li>
              <li>
                <button
                  onClick={() => {
                    localStorage.removeItem('dizee-cookie-consent')
                    window.location.reload()
                  }}
                  className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-gray-500 rounded"
                >
                  Cookie Preferences
                </button>
              </li>
            </ul>
          </nav>
          <p className="mt-6 text-xs text-gray-500">© {new Date().getFullYear()} DIZEE. All rights reserved.</p>
        </footer>
      </div>
    </div>
  )
}

// Helper components for legal content structure
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{title}</h2>
      {children}
    </section>
  )
}

export function SubSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">{title}</h3>
      {children}
    </div>
  )
}

export function Paragraph({ children }: { children: ReactNode }) {
  return <p className="text-sm mt-4">{children}</p>
}

export function List({ items }: { items: string[] }) {
  return (
    <ul className="list-disc list-inside ml-4 mt-3 space-y-2">
      {items.map((item, idx) => (
        <li key={idx} className="text-sm">
          {item}
        </li>
      ))}
    </ul>
  )
}
